import { useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type {
  FinancialManagementState,
  FixedIncomeUpdateRequest,
  ProductFinanceClient,
} from '#components/product-finance/client';
import {
  formatMoneyInput,
  formatProductMoney,
  parseMoneyInput,
} from '#components/product-finance/money';

import { getManagementErrorMessage } from './management-errors';
import {
  buttonRowStyle,
  errorStyle,
  Field,
  formStyle,
  helpStyle,
  inputStyle,
  ManagementCard,
} from './management-shared';
import {
  createRecurrenceRequest,
  formatRecurrence,
  readFrequency,
} from './recurrence-fields';
import type { RecurrenceFrequency } from './recurrence-fields';

export type ManagedIncome = FinancialManagementState['incomeTemplates'][number];

export function IncomeManagement({
  state,
  client,
  onChanged,
}: Readonly<{
  state: FinancialManagementState;
  client: ProductFinanceClient;
  onChanged: () => Promise<void>;
}>) {
  const [editing, setEditing] = useState<ManagedIncome | null>(null);
  return (
    <View style={{ gap: 12 }}>
      <ManagementCard title={<Trans>Fixed incomes</Trans>}>
        <p style={helpStyle}>
          <Trans>
            Changes apply to future generated expectations and never rewrite
            recorded income.
          </Trans>
        </p>
      </ManagementCard>
      {editing !== null && (
        <IncomeForm
          key={editing.id}
          template={editing}
          client={client}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChanged();
          }}
        />
      )}
      {state.incomeTemplates.map(template => (
        <IncomeCard
          key={template.id}
          template={template}
          client={client}
          onEdit={() => setEditing(template)}
          onChanged={onChanged}
        />
      ))}
    </View>
  );
}

export function IncomeCard({
  template,
  client,
  onEdit,
  onChanged,
}: Readonly<{
  template: ManagedIncome;
  client: ProductFinanceClient;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}>) {
  const { t } = useTranslation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  async function toggle() {
    setSubmitting(true);
    setError(false);
    try {
      await client.setFixedIncomeTemplateEnabled({
        id: template.id,
        enabled: !template.enabled,
      });
      await onChanged();
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <Card style={{ margin: 0 }}>
      <View style={{ padding: 16, gap: 8 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <View>
            <strong>{template.name}</strong>
            <span style={helpStyle}>
              {formatRecurrence(template.recurrence)} ·{' '}
              {template.enabled ? t('Enabled') : t('Disabled')}
            </span>
          </View>
          <FinancialText as="strong">
            {formatProductMoney(template.amount)}
          </FinancialText>
        </View>
        {error && (
          <p role="alert" style={errorStyle}>
            <Trans>
              This income still anchors an enabled cycle or could not be saved.
            </Trans>
          </p>
        )}
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Button onPress={onEdit}>
            <Trans>Edit</Trans>
          </Button>
          <ButtonWithLoading
            isLoading={isSubmitting}
            onPress={() => void toggle()}
          >
            {template.enabled ? <Trans>Disable</Trans> : <Trans>Enable</Trans>}
          </ButtonWithLoading>
        </View>
      </View>
    </Card>
  );
}

export function IncomeForm({
  template,
  client,
  onCancel,
  onSaved,
}: Readonly<{
  template: ManagedIncome;
  client: ProductFinanceClient;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}>) {
  const { t } = useTranslation();
  const recurrence = template.recurrence;
  const [name, setName] = useState(template.name);
  const [amount, setAmount] = useState(formatMoneyInput(template.amount));
  const [frequency, setFrequency] = useState<RecurrenceFrequency>(
    recurrence.frequency,
  );
  const [dayOfWeek, setDayOfWeek] = useState(
    recurrence.frequency === 'weekly' ? String(recurrence.dayOfWeek) : '1',
  );
  const [dayOfMonth, setDayOfMonth] = useState(
    recurrence.frequency === 'monthly' || recurrence.frequency === 'yearly'
      ? String(recurrence.dayOfMonth)
      : '1',
  );
  const [month, setMonth] = useState(
    recurrence.frequency === 'yearly' ? String(recurrence.month) : '1',
  );
  const [startDate, setStartDate] = useState<string>(template.startDate);
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amountMinorUnits = parseMoneyInput(amount);
    if (amountMinorUnits === null) {
      setError(t('Enter a valid positive amount.'));
      return;
    }
    const input: FixedIncomeUpdateRequest = {
      id: template.id,
      name,
      amountMinorUnits,
      currency: template.amount.currency,
      recurrence: createRecurrenceRequest(
        frequency,
        dayOfWeek,
        dayOfMonth,
        month,
      ),
      startDate,
      enabled: template.enabled,
    };
    setSubmitting(true);
    setError(null);
    try {
      await client.updateFixedIncomeTemplate(input);
      await onSaved();
    } catch (cause) {
      setError(getManagementErrorMessage(cause, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ManagementCard title={<Trans>Edit income</Trans>}>
      <form onSubmit={submit} style={formStyle}>
        <Field label={<Trans>Name</Trans>} htmlFor="income-name">
          <input
            id="income-name"
            value={name}
            onChange={event => setName(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label={<Trans>Amount</Trans>} htmlFor="income-amount">
          <input
            id="income-amount"
            inputMode="decimal"
            value={amount}
            onChange={event => setAmount(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label={<Trans>Frequency</Trans>} htmlFor="income-frequency">
          <select
            id="income-frequency"
            aria-label={t('Frequency')}
            value={frequency}
            onChange={event => setFrequency(readFrequency(event.target.value))}
            style={inputStyle}
          >
            <option value="weekly">
              <Trans>Weekly</Trans>
            </option>
            <option value="monthly">
              <Trans>Monthly</Trans>
            </option>
            <option value="yearly">
              <Trans>Yearly</Trans>
            </option>
          </select>
        </Field>
        {frequency === 'weekly' && (
          <Field label={<Trans>Day of week</Trans>} htmlFor="income-weekday">
            <input
              id="income-weekday"
              type="number"
              min="1"
              max="7"
              value={dayOfWeek}
              onChange={event => setDayOfWeek(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        {(frequency === 'monthly' || frequency === 'yearly') && (
          <Field label={<Trans>Day of month</Trans>} htmlFor="income-monthday">
            <input
              id="income-monthday"
              type="number"
              min="1"
              max="31"
              value={dayOfMonth}
              onChange={event => setDayOfMonth(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        {frequency === 'yearly' && (
          <Field label={<Trans>Month</Trans>} htmlFor="income-month">
            <input
              id="income-month"
              type="number"
              min="1"
              max="12"
              value={month}
              onChange={event => setMonth(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        <Field label={<Trans>Start date</Trans>} htmlFor="income-start">
          <input
            id="income-start"
            type="date"
            value={startDate}
            onChange={event => setStartDate(event.target.value)}
            style={inputStyle}
          />
        </Field>
        {error !== null && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}
        <View style={buttonRowStyle}>
          <Button type="button" onPress={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <ButtonWithLoading
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            <Trans>Save income</Trans>
          </ButtonWithLoading>
        </View>
      </form>
    </ManagementCard>
  );
}
